import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import { SiteDashboard, SiteDashboardAttributes } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";

import { QUERY_KEYS } from "../../../constants";
import {
  DecodedWidgetConfig,
  WidgetConfig,
} from "../../../types/DecodedWidgetConfig.type";

export const useUpdateDashboard = (
  siteId: number,
  dashboard: SiteDashboard,
) => {
  const queryClient = useQueryClient();

  const { mutateAsync: updateDashboardAsync } = useMutation({
    mutationFn: async (update: SiteDashboardAttributes) => {
      await apiClient.sites.dashboardsPartialUpdate(
        siteId,
        dashboard.id,
        update,
      );

      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DASHBOARDS(siteId),
      });
    },
  });

  const updateWidgets = useCallback(
    async (configs: DecodedWidgetConfig[]) => {
      return updateDashboardAsync({
        name: dashboard.name,
        properties: {
          widgets: configs.map(fromDecodedWidgetConfig),
        },
      });
    },
    [updateDashboardAsync, dashboard.name],
  );

  const updateSingleWidget = useCallback(
    async (updatedWidget: DecodedWidgetConfig) => {
      const updatedWidgets = (dashboard.properties?.widgets || []).map(
        (widget) =>
          widget.id === updatedWidget.id
            ? fromDecodedWidgetConfig(updatedWidget)
            : widget,
      );

      return updateDashboardAsync({
        name: dashboard.name,
        properties: {
          widgets: updatedWidgets,
        },
      });
    },
    [updateDashboardAsync, dashboard.name, dashboard.properties?.widgets],
  );

  const updateName = useCallback(
    async (name: string) => {
      return updateDashboardAsync({
        name,
        properties: {
          widgets: dashboard.properties?.widgets ?? [],
        },
      });
    },
    [updateDashboardAsync, dashboard.properties?.widgets],
  );

  return {
    updateWidgets,
    updateSingleWidget,
    updateName,
  };
};

const fromDecodedWidgetConfig = (config: DecodedWidgetConfig): WidgetConfig => {
  return {
    ...config,
    integration: config.integration,
    method: config.method,
    // @ts-expect-error - BE types
    query: config.query,
  };
};
